import React, { useState } from 'react';
import styled from 'styled-components';
import {
  PageLayout,
  MainContent,
  ContentContainer,
  PageTitle,
} from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  InfoOutlined,
  Download,
  Visibility,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

interface ServiceDetails {
  name: string;
  logo: string;
  establishedYear: string;
  location: string;
  website: string;
  phone: string;
  email: string;
  emailSettings: {
    fromName: string;
    replyToEmail: string;
    emailSignature: string;
  };
  notifications: {
    newLeads: boolean;
    newMeetings: boolean;
    dailyDigest: boolean;
  };
}

const Section = styled.div`
  margin-bottom: 2rem;
`;

const CopyButton = styled(IconButton)`
  && {
    padding: 6px;
  }
`;

const PreviewButton = styled(IconButton)`
  && {
    padding: 6px;
  }
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const QRCodeContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
`;

const InfoIcon = styled(InfoOutlined)`
  && {
    font-size: 1.25rem;
    color: #718096;
    margin-left: 0.5rem;
  }
`;

const ImagePreview = styled.div`
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  img {
    height: 30px;
  }
`;

const ServiceDetailsPage: React.FC = () => {
  const [settings, setSettings] = useState<ServiceDetails>({
    name: 'Wanderlust Adventures',
    logo: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=300&q=80',
    establishedYear: '2015',
    location: 'Sydney, Australia',
    website: 'www.wanderlustadventures.com',
    phone: '+1 (555) 123-4567',
    email: 'info@wanderlustadventures.com',
    emailSettings: {
      fromName: 'Wanderlust Adventures',
      replyToEmail: 'support@wanderlustadventures.com',
      emailSignature: 'Best regards,\nThe Wanderlust Adventures Team',
    },
    notifications: {
      newLeads: true,
      newMeetings: true,
      dailyDigest: false,
    },
  });

  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const baseUrl = 'https://triplink.vip/';
  const currentUsername = 'jazzy_globtrotter';
  const expertUrl = `${baseUrl}${currentUsername}`;

  const handleSettingChange = (path: string[], value: any) => {
    const newSettings = { ...settings };
    let current: any = newSettings;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
    setSettings(newSettings);
  };

  type SizeType = '1x' | '2x' | '4x';
  const sizeMap: Record<SizeType, string> = {
    '1x': 'X1',
    '2x': 'X2',
    '4x': 'X4'
  };

  const handleDownload = (variantName: string) => () => {
    const filename = `${variantName}.zip`;
    const link = document.createElement('a');
    link.href = `/assets/zip/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePreview = (url: string) => {
    window.open(url, '_blank');
  };

  const downloadQRCode = () => {
    const svgElement = document.querySelector("#meetingQRCode")?.querySelector("svg");
    if (!svgElement) return;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    // Create a temporary link to download
    const link = document.createElement("a");
    link.href = url;
    link.download = "qrcode.svg"; // Saves as an SVG file
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const lightImageVariants = [
    { name: 'Black_Transparent', preview: '/assets/images/Black_Transparent.svg', filename: 'Black_Transparent' },
    { name: 'White_Black', preview: '/assets/images/White_Black.svg', filename: 'White_Black' },
    { name: 'Green', preview: '/assets/images/Green.svg', filename: 'Green' },
    { name: 'Gold_Black', preview: '/assets/images/Gold_Black.svg', filename: 'Gold_Black' },
  ];

  const darkImageVariants = [
    { name: 'White_Transparent', preview: '/assets/images/White_Transparent.svg', filename: 'White_Transparent' },
    { name: 'Black_White', preview: '/assets/images/Black_White.svg', filename: 'Black_White' },
    { name: 'Black_Gold', preview: '/assets/images/Black_Gold.svg', filename: 'Black_Gold' },
  ]

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Service Details</PageTitle>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box>
                    <SectionTitle>
                      Primary Triplink
                      <Tooltip title="Share this link with your clients to collect their information">
                        <InfoIcon />
                      </Tooltip>
                    </SectionTitle>

                    <LinkContainer>
                      <Typography>{baseUrl + currentUsername}</Typography>
                      <CopyButton onClick={() => handleCopy(baseUrl + currentUsername)}>
                        <ContentCopyIcon />
                      </CopyButton>
                      <PreviewButton onClick={() => handlePreview(baseUrl + currentUsername)}>
                        <Visibility />
                      </PreviewButton>
                    </LinkContainer>
                    <QRCodeContainer id='meetingQRCode'>
                      <QRCodeSVG value={expertUrl} size={128} />
                      <Button variant='contained' onClick={downloadQRCode} sx={{ minWidth: 0, aspectRatio: 1, width: 35, height: 35 }}><Download /></Button>
                    </QRCodeContainer>
                  </Box>
                  <Box>
                    <SectionTitle>
                      Download Brand Assets
                      <Tooltip title="Download these assets to use in your marketing materials">
                        <InfoIcon />
                      </Tooltip>
                    </SectionTitle>
                    <Typography fontSize={14}>Use images with you link on existing digital spaces (websites, social media, email signatures etc.)</Typography>

                    <Grid container xs={12} sx={{ marginTop: 2 }}>
                      <Grid item md={6} sm={12} sx={{ borderRadius: 2, borderWidth: 2 }}>
                        {lightImageVariants.map((variant) => (
                          <ImagePreview key={variant.name}>
                            <img src={variant.preview} alt={variant.name} />
                            <Button variant='contained' onClick={handleDownload(variant.filename)} sx={{ minWidth: 0, aspectRatio: 1, width: 35, height: 35 }}><Download /></Button>
                          </ImagePreview>
                        ))}
                      </Grid>
                      <Grid item md={6} sm={12} sx={{ backgroundColor: '#1e242a', borderRadius: 2, borderWidth: 2 }}>
                        {darkImageVariants.map((variant) => (
                          <ImagePreview key={variant.name}>
                            <img src={variant.preview} alt={variant.name} style={{ backgroundColor: 'black' }} />
                            <Button variant='contained' onClick={handleDownload(variant.filename)} sx={{ minWidth: 0, aspectRatio: 1, width: 35, height: 35 }}><Download /></Button>
                          </ImagePreview>
                        ))}
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <SectionTitle mb={3}>Customer Notification Elements</SectionTitle>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="From Name"
                        value={settings.emailSettings.fromName}
                        onChange={(e) => handleSettingChange(['emailSettings', 'fromName'], e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Reply-To Email"
                        type="email"
                        value={settings.emailSettings.replyToEmail}
                        onChange={(e) => handleSettingChange(['emailSettings', 'replyToEmail'], e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Signature"
                        multiline
                        rows={4}
                        value={settings.emailSettings.emailSignature}
                        onChange={(e) => handleSettingChange(['emailSettings', 'emailSignature'], e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <SectionTitle>Notification Preferences</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notifications.newLeads}
                            onChange={(e) => handleSettingChange(['notifications', 'newLeads'], e.target.checked)}
                          />
                        }
                        label="New Lead Notifications"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notifications.newMeetings}
                            onChange={(e) => handleSettingChange(['notifications', 'newMeetings'], e.target.checked)}
                          />
                        }
                        label="New Meeting Notifications"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notifications.dailyDigest}
                            onChange={(e) => handleSettingChange(['notifications', 'dailyDigest'], e.target.checked)}
                          />
                        }
                        label="Daily Digest Email"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={4}>
            <Button variant="contained" color="primary" size="large">
              Save Changes
            </Button>
          </Box>
        </ContentContainer>
      </MainContent>
    </PageLayout>
  );
};

const SectionTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  color: #2D3748;
`;

export default ServiceDetailsPage;
